import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Circle } from 'lucide-react';

const FaShowRecorder = () => {
  const FA_SHOW_URL = "https://fa-show-orchestra.lovable.app/";

  return (
    <div className="min-h-screen bg-[#0a0a14] relative overflow-hidden">
      {/* Subtle gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#0a0a14] via-[#0d0d1a] to-[#0a0a14]" />
      
      {/* Animated glow effects */}
      <div className="absolute inset-0">
        <motion.div 
          className="absolute top-1/3 left-1/4 w-[500px] h-[500px] rounded-full bg-[#6366f1]/5 blur-3xl"
          animate={{ 
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3]
          }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div 
          className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] rounded-full bg-[#818cf8]/5 blur-3xl"
          animate={{ 
            scale: [1, 1.3, 1],
            opacity: [0.2, 0.4, 0.2]
          }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut", delay: 1 }}
        />
      </div>

      {/* Content */}
      <div className="relative z-10">
        {/* Header */}
        <div className="absolute top-6 left-6">
          <Link 
            to="/" 
            className="flex items-center gap-2 text-[#555] hover:text-white transition-colors text-sm"
            data-testid="back-home-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Home</span>
          </Link>
        </div>

        {/* Main Content */}
        <div className="min-h-screen flex flex-col items-center justify-center px-4 py-20">
          
          {/* Version tag */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="flex items-center gap-3 text-[#666] text-sm font-mono mb-8"
          >
            <span className="text-[#6366f1]">——</span>
            <span>V1 · VOICE → INSTRUMENT ENGINE</span>
          </motion.div>

          {/* Main Headline - Fa Show style */}
          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="text-7xl md:text-9xl lg:text-[12rem] font-black text-center mb-6 tracking-tight leading-none"
          >
            <span className="text-white font-light">Fa </span>
            <motion.span 
              className="text-[#818cf8] italic"
              animate={{ 
                textShadow: [
                  "0 0 30px rgba(129,140,248,0)",
                  "0 0 60px rgba(129,140,248,0.3)",
                  "0 0 30px rgba(129,140,248,0)"
                ]
              }}
              transition={{ duration: 3, repeat: Infinity }}
            >
              Show
            </motion.span>
          </motion.h1>

          {/* Tagline */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mb-8"
          >
            <p className="text-white/90 text-2xl md:text-3xl italic font-light">
              You say it.
            </p>
            <p className="text-white/90 text-2xl md:text-3xl italic font-light border-b-2 border-[#6366f1] pb-2 inline-block">
              We play it.
            </p>
          </motion.div>

          {/* Description */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="text-[#888] text-lg md:text-xl text-center max-w-md mb-12"
          >
            Hum a line. Sing a riff. Whisper a melody. Fa Show turns your voice into a cello, a five-piece band, or a ninety-piece orchestra — instantly.
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="flex flex-col sm:flex-row items-center gap-4"
          >
            {/* Primary Button */}
            <motion.a
              href={FA_SHOW_URL}
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.98 }}
              className="group flex items-center gap-3 px-8 py-4 bg-[#6366f1] text-white text-lg font-semibold rounded-full 
                         shadow-[0_0_30px_rgba(99,102,241,0.3)] hover:shadow-[0_0_50px_rgba(99,102,241,0.5)]
                         transition-all duration-300"
              data-testid="start-recording-btn"
            >
              <Circle className="w-3 h-3 fill-current" />
              <span>Start recording</span>
            </motion.a>

            {/* Secondary Button */}
            <motion.a
              href={FA_SHOW_URL}
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.03, backgroundColor: "rgba(99,102,241,0.1)" }}
              whileTap={{ scale: 0.98 }}
              className="flex items-center gap-2 px-8 py-4 border border-[#333] text-white text-lg font-medium rounded-full
                         transition-all duration-300 hover:border-[#6366f1]"
              data-testid="see-how-btn"
            >
              <span>See how it works</span>
            </motion.a>
          </motion.div>

          {/* Bottom note */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
            className="mt-12 text-[#555] text-sm font-mono"
          >
            [ no login · works in the browser ]
          </motion.p>

        </div>
      </div>
    </div>
  );
};

export default FaShowRecorder;
